/* eslint-disable no-param-reassign */
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { NEW_PATH_DELIMITER } from '@picsio/db/src/constants';
import {
  makeTree, setField, DEFAULT_SAVED_SEARCH_TREE_SORT,
} from '../helpers/savedSearches';
import resolveParentPathForUndoRestore from '../helpers/savedSearches/resolveParentPathForUndoRestore';
import { addSavedSearch } from './add';

/**
 * Mirrors collections `COLLECTIONS.REMOVE.START`: mark the tree row busy only
 * after the user confirms removal and the delete request is in flight.
 */
export const setSavedSearchRemoving = createAction(
  'savedSearches/setRemoving',
  ({ _id, isRemoving }) => ({ payload: { _id, isRemoving } }),
);

export const remove = createAsyncThunk(
  'savedSearches/remove',
  async (
    {
      _id, name, showToast = true, showDialog = true,
    },
    {
      extra: {
        sdk, Logger, Toast, localization: l18n, showErrorDialog, showDialogAsync, utils,
      },
      getState, dispatch,
    },
  ) => {
    if (showDialog) {
      const dialogTitle = l18n.TAGSTREE.titleRemoveSavedSearch;
      const dialogText = l18n.TAGSTREE.textYouAreAboutRemoveSavedSearch(name);
      try {
        Logger.log('UI', 'ConfirmDeleteSavedSearch');
        await showDialogAsync({
          title: dialogTitle,
          text: dialogText,
          textBtnOk: l18n.DIALOGS.btnDelete,
          textBtnCancel: l18n.DIALOGS.btnCancel,
          onCancel: () => {
            Logger.log('User', 'SavedSearchesDeleteSSModalClosed');
          },
          onOk: () => {
            Logger.log(
              'User',
              'SavedSearchesDeleteSSModalDeleteClicked',
            );
          },
        }).promise;
        Logger.log('User', 'SavedSearchesDeleteSSModalShown');
      } catch (error) {
        // Do nothing, user just closed the dialog
        Logger.log('User', 'ConfirmDeleteSavedSearchNo');
        return null;
      }
      Logger.log('User', 'ConfirmDeleteSavedSearchYes', { savedSearchId: _id });
    }

    dispatch(setSavedSearchRemoving({ _id, isRemoving: true }));

    try {
      const { data: result } = await sdk.savedSearches.delete(_id);
      if (showToast) {
        const nodes = getState().savedSearches.all;
        const node = nodes.find((ss) => ss._id === _id);
        const rawParentPathForRestore = node.path
          ? node.path.split(NEW_PATH_DELIMITER).slice(0, -1).join(NEW_PATH_DELIMITER)
          : '';
        Toast(l18n.TAGSTREE.textHasBeenRemoved(name), {
          type: 'success',
          btnOkValue: l18n.textBtnUndo,
          onOk: () => {
            const path = resolveParentPathForUndoRestore(
              getState().savedSearches.all,
              Boolean(node.teamId),
              rawParentPathForRestore,
            );
            dispatch(addSavedSearch({
              name,
              data: node.data,
              entity: node.entity,
              shared: Boolean(node.teamId),
              path,
              showToast: false,
            }));
          },
        });
      }
      return { result, _id, userId: getState().user._id };
    } catch (error) {
      dispatch(setSavedSearchRemoving({ _id, isRemoving: false }));
      const errorStatus = utils.getStatusFromResponceError(error);
      if (errorStatus === 403) {
        showErrorDialog(l18n.NO_PERMISSION_TO_ACCESS);
      } else {
        Logger.error(new Error('Can not remove saved searches'), { error }, [
          'RemoveSavedSearchesFailed',
          { errorMessage: (error && error.message) || 'NoMessage' },
        ]);
      }

      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder.addCase(
    setSavedSearchRemoving,
    (state, { payload: { _id, isRemoving } }) => {
      state.tree = setField(state.tree, _id, 'isBusy', isRemoving);
    },
  ).addCase(
    remove.fulfilled,
    (state, { payload, meta: { arg: { _id } } }) => {
      if (!payload) {
        return;
      }
      const { userId } = payload;
      state.all = state.all.filter((ss) => ss._id !== _id);
      state.tree = makeTree(
        state.all,
        userId,
        state.searchQuery,
        state.treeSortType || DEFAULT_SAVED_SEARCH_TREE_SORT,
      );
    },
  ).addCase(
    remove.rejected,
    (state, { error, meta: { arg: { _id } } }) => {
      state.error = error;
      state.tree = setField(state.tree, _id, 'isBusy', false);
    },
  );
};
